<template>
  <div>
    <b-form-row>
      <partial-checkbox
        :allow-selection="allowSelection"
        :selected="partiallySelection.selected"
        :state="partiallySelection.state"
        :disabled="serviceSelectionDisabled"
        @change="(data) => onPartialInvoiceChange(data)"
      />
      <b-col cols="12" :md="job_sequence ? 9 : 6" class="mb-2">
        <b-form-group class="mb-0">
          <multiselect
            v-e2e:selectService
            v-model="activeServiceSfid"
            label="text"
            :style="differentInvoicingTargetStyle"
            track-by="value"
            :options="filteredServicesOptions"
            :disabled="
              !editServices ||
              readonly ||
              isRequiredBy ||
              (context !== 'admin' && isNotNew)
            "
          />
          <div
            class="gid-service-hints bg-light small text-muted border mt-n1 border-top-0 rounded-bottom pt-1 pb-1 px-2"
            v-if="showServiceHints"
          >
            <div
              class="flex-grow-1"
              v-if="serviceDefinition && serviceDefinition.hints[locale]"
            >
              <b>{{ $t('inputs.services.hints') }}:</b>
              <div v-html="serviceDefinition.hints[locale]"></div>
            </div>
            <div>
              <div>
                <b>{{ $t('inputs.services.service-type') }}:</b>
                <div v-html="serviceDefinition.service_type"></div>
              </div>
              <div v-if="showInvoicingTarget">
                <b>{{ $t('inputs.services.invoicing-type') }}:</b>
                <div v-html="currentInvoicingTarget"></div>
              </div>
              <div v-if="!useExternalNames">
                <b>{{ $t('inputs.services.extern-title') }}</b>
                <div>{{ serviceDefinition.description[locale] }}</div>
              </div>
              <div>
                <a :href="normServiceURL" target="_blank">
                  Norm Service
                  <i class="material-icons">edit</i>
                </a>
              </div>
            </div>
          </div>
          <b-badge
            v-if="isDifferentInvoicingTarget && serviceDefinition"
            v-b-tooltip.hover
            :title="`Current Invoicing Target in System: ${serviceDefinition.invoicing_target}`"
            variant="warning"
          >
            {{ $t('Different invoicing target') }}
          </b-badge>
          <span slot="description" v-if="servicesGroup && serviceDefinition">
            {{
              useExternalNames
                ? serviceDefinition.description[locale] ||
                  serviceDefinition.description.en
                : serviceDefinition.name
            }}
          </span>
        </b-form-group>
      </b-col>

      <b-col cols="12" md="3" class="mb-2" v-if="job_sequence">
        <b-form-input
          v-e2e:serviceBrandProjectId
          v-model="brandProjectId"
          title="Brand Project ID"
          v-b-tooltip.hover
        />
      </b-col>
      <b-col cols="3" md class="mb-2" v-if="usesQuantity">
        <b-form-input
          v-e2e:serviceQuantity
          v-model="quantity"
          type="number"
          :min="minQuantity"
          :max="maxQuantity"
          :step="stepQuantity"
          :disabled="
            !editServices ||
            readonly ||
            minQuantity == maxQuantity ||
            (context == 'partner' && !isEditableForPartner)
          "
        />
      </b-col>
      <b-col cols="3" md class="mb-2" v-if="usesValueEntry">
        <b-form-input
          v-e2e:serviceValueEntry
          v-model="value_entry"
          type="number"
          :disabled="!editServices || readonly"
          :min="minEntryQuantity"
          :max="maxEntryQuantity"
        />
      </b-col>
      <b-col class="mb-2 gid-tiny-col">
        <b-form-input
          :value="measure"
          :readonly="true"
          :plaintext="true"
          tabindex="-1"
        />
      </b-col>
      <b-col class="mb-2" v-if="!hidePriceList">
        <b-form-input
          v-e2e:servicePriceList
          class="text-right"
          :style="price_lists_style"
          v-model="price_lists"
          :readonly="!editPriceList"
          :title="isVariantPriceLists ? priceListsText : 'List price'"
          v-b-tooltip.hover="
            isVariantPriceLists
              ? { variant: 'warning' }
              : { variant: 'secondary' }
          "
        />
      </b-col>
      <b-col class="mb-2" v-if="!hidePriceSelling">
        <b-form-input
          v-e2e:servicePriceSelling
          class="text-right"
          v-model="price_selling"
          :style="price_selling_style"
          :readonly="!editPriceSelling"
          :title="isVariantPriceSelling ? priceSellingText : 'Selling price'"
          v-b-tooltip.hover="
            isVariantPriceSelling
              ? { variant: 'warning' }
              : { variant: 'secondary' }
          "
        />
      </b-col>
      <b-col class="mb-2" v-if="!hidePricePurchasing">
        <b-form-input
          class="text-right"
          v-e2e:servicePricePurchasing
          v-model="price_purchasing"
          :readonly="!editPricePurchasing"
          :style="price_purchasing_style"
          :title="
            isVariantPricePurchasing ? pricePurchasingText : 'Purchasing price'
          "
          v-b-tooltip.hover="
            isVariantPricePurchasing
              ? { variant: 'warning' }
              : { variant: 'secondary' }
          "
        />
      </b-col>
      <b-col class="mb-2" v-if="showTotals">
        <b-form-input
          class="text-right"
          :value="total | currency"
          :readonly="true"
          :plaintext="true"
          tabindex="-1"
        />
      </b-col>
      <b-col :class="['mb-2', 'gid-tiny-col', { invisible: !deleteVisible }]">
        <b-dropdown
          v-if="allowCrediting"
          variant="outline-secondary"
          ref="removeActionsDropdown"
        >
          <template v-slot:button-content>
            <i class="material-icons">delete</i>
          </template>
          <b-dropdown-item-button
            :disabled="
              deleteDisabled ||
              isRequiredBy ||
              (context == 'partner' && !isEditableForPartner)
            "
            @click="deleteService()"
          >
            Remove
          </b-dropdown-item-button>
          <b-dropdown-form>
            <b-form-checkbox
              v-model="creditListsPrice"
              :disabled="price_lists === null || price_lists === undefined"
            >
              List Price
            </b-form-checkbox>
            <b-form-checkbox
              v-model="creditSellingPrice"
              :disabled="price_selling === null || price_selling === undefined"
            >
              Selling Price
            </b-form-checkbox>
            <b-form-checkbox
              v-model="creditPurchasingPrice"
              :disabled="
                price_purchasing === null || price_purchasing === undefined
              "
            >
              Purchase Price
            </b-form-checkbox>

            <b-button
              variant="danger"
              :disabled="
                !(
                  creditListsPrice ||
                  creditPurchasingPrice ||
                  creditSellingPrice
                )
              "
              @click="creditService()"
            >
              Credit
            </b-button>
          </b-dropdown-form>
        </b-dropdown>
        <b-button
          v-else
          variant="outline-secondary"
          @click="deleteService()"
          :disabled="deleteDisabled || isRequiredBy"
          title="Remove"
        >
          <i class="material-icons">delete</i>
        </b-button>
      </b-col>
    </b-form-row>
    <b-form-row v-if="context == 'admin' && editInvoicingTarget">
      <b-col md>
        <b-form-group label="Invoiced to">
          <router-link
            v-if="invoicingTargetAccount.name"
            :to="`/data-ui/${invoicingTargetEntity}/${invoicingTargetAccount.id}`"
            target="_blank"
            rel="noreferrer"
          >
            {{ invoicingTargetAccount.name }}
          </router-link>
          <span v-else>N/A</span>
        </b-form-group>
      </b-col>
      <b-col md>
        <b-form-group label="Invoicing target">
          <multiselect
            v-model="invoicingTarget"
            :disabled="!editInvoicingTarget || readonly"
            :show-labels="false"
            :options="invoicingTargets"
          />
        </b-form-group>
      </b-col>
    </b-form-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import BaseInput from './mixins/BaseInput';
import PartialCheckbox, { PartialStateEnum } from './common/PartialCheckbox';

import {
  SERVICE_SELECTED_ITEM_DELETE,
  SERVICE_SELECTED_SPECIFY,
  SERVICE_PRESELECT_AUTOMATION,
  SERVICE_CHANGE_QUANTITY,
} from '../../store/services.module/types';
import { JOB_SEQUENCE } from './_types';
import { toGenericInvoicingTarget, InvoicingTargetEnum } from '@gid/models';
import { normalizeServiceList } from '@gid/api-client';
import { loadPartialSelection } from '../../utils/partial_selection';
import { SelectActionsEnum } from '@gid/models/dist/entities/job-selection';

function nullOrUndefined(value) {
  return value === null || value === undefined || value === '';
}

export default {
  components: {
    PartialCheckbox,
  },
  mixins: [BaseInput],
  props: {
    elementIndex: Number,
    selectedServices: Array,
    servicesOptions: Array,
    hidePricePurchasing: Boolean,
    hidePriceSelling: Boolean,
    hidePriceList: {
      type: Boolean,
      default: true,
    },
    editServices: {
      type: Boolean,
      default: true,
    },
    jobId: {
      type: String,
      default: 'new',
    },
    editPriceSelling: Boolean,
    editPricePurchasing: Boolean,
    editPriceList: Boolean,
    showServiceHints: Boolean,
    editInvoicingTarget: Boolean,
    productId: {
      type: String,
      default: undefined,
    },
    showTotals: Boolean,
    allowCrediting: {
      type: Boolean,
      default: false,
    },
    job_view_services: Array,
    context: String,
    isNotNew: Boolean,
    allowSelection: {
      type: Boolean,
      default: false,
    },
    selections: {
      type: Array,
      default: () => [],
    },
    service: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      creditListsPrice: false,
      creditSellingPrice: false,
      creditPurchasingPrice: false,
    };
  },
  computed: {
    ...mapGetters(['locale', 'useExternalNames']),
    filteredServicesOptions() {
      const alreadySelectedServices = this.$store.getters
        .allSelectedServices(this.jobId)
        .map((service) => service.service);
      return this.servicesOptions.filter(
        (option) =>
          !alreadySelectedServices.includes(option.value) ||
          this.activeService.service === option.value,
      );
    },
    chosenServiceName() {
      return (
        this.activeServiceSfid &&
        this.filteredServicesOptions.find(
          (o) => o.sfid == this.activeServiceSfid.value,
        ).text
      );
    },
    activeService() {
      return this.$store.getters.activeService({
        elementIndex: this.elementIndex,
        jobId: this.jobId,
        productId: this.productId,
      });
    },
    normServiceURL() {
      return `../data-ui/Service/${this.activeService.service}`;
    },
    activeServiceSfid: {
      get() {
        return this.filteredServicesOptions.find(
          (o) => o.value == this.activeService.service,
        );
      },
      set({ value }) {
        const serviceDef = this.serviceDefinitions[value];
        this.$store.dispatch(SERVICE_SELECTED_SPECIFY, {
          jobId: this.jobId,
          elementIndex: this.elementIndex,
          isNew: true,
          newService: {
            productId: this.productId,
            quantity: this.quantity,
            service: value,
            value_entry: this.value_entry,
            price_lists: serviceDef.price_lists,
            price_purchasing: serviceDef.price_purchasing,
            price_selling: serviceDef.price_selling,
            invoicing_target: serviceDef.invoicing_target,
          },
        });
      },
    },
    invoicingTargets() {
      return Object.values(InvoicingTargetEnum);
    },
    invoicingTargetAccount() {
      const t = this.$store.getters.jobInvoicingTargets(this.jobId);
      if (!t) {
        return {};
      }
      return t.legacy[this.currentInvoicingTarget] ?? {};
    },
    pricePurchasingText() {
      return `Current Price in System: ${this.serviceDefinition?.price_purchasing}€`;
    },
    priceSellingText() {
      return `Current Price in System: ${this.serviceDefinition?.price_selling}€`;
    },
    priceListsText() {
      return `Current Price in System: ${this.serviceDefinition?.price_lists}€`;
    },
    currentInvoicingTarget() {
      if (this.activeService?.invoicing_target) {
        return this.activeService?.invoicing_target;
      }
      if (this.job_view_services) {
        return this.job_view_services.find(
          (js) => js.sfid === this.activeServiceSfid?.value,
        )?.invoicing_target;
      }
      return this.serviceDefinition?.invoicing_target;
    },
    isRequiredBy: {
      get() {
        return !!this.activeService?.requiredBy;
      },
    },
    quantity: {
      get() {
        if (this.activeService) {
          return this.activeService.quantity;
        }
        return 0;
      },
      set(value) {
        this.$store.dispatch(SERVICE_CHANGE_QUANTITY, {
          elementIndex: this.elementIndex,
          jobId: this.jobId,
          newService: {
            productId: this.productId,
            quantity: value,
            service: this.activeService.service,
          },
        });
      },
    },
    price_lists: {
      get() {
        return this.activeService.price_lists !== undefined
          ? this.activeService.price_lists
          : this.selectedService.price_lists;
      },
      set(value) {
        this.$store.dispatch(SERVICE_SELECTED_SPECIFY, {
          elementIndex: this.elementIndex,
          jobId: this.jobId,
          newService: {
            productId: this.productId,
            quantity: this.quantity,
            price_lists: !nullOrUndefined(value) ? Number(value) : null,
            service: this.activeService.service,
          },
        });
      },
    },
    price_lists_style() {
      let style = null;
      if (
        this.currentInvoicingTarget &&
        this.currentInvoicingTarget === 'Kunde'
      ) {
        style = { 'background-color': '#D4EDDA' };
      }
      if (this.isVariantPriceLists) {
        style = { ...style, border: '2px solid #fd7e14' };
      }
      return style;
    },
    price_purchasing_style() {
      let style = null;
      if (this.isVariantPricePurchasing) {
        style = { ...style, border: '2px solid #fd7e14' };
      }
      return style;
    },
    price_purchasing: {
      get() {
        return this.activeService.price_purchasing !== undefined
          ? this.activeService.price_purchasing
          : this.selectedService.price_purchasing;
      },
      set(value) {
        this.$store.dispatch(SERVICE_SELECTED_SPECIFY, {
          elementIndex: this.elementIndex,
          jobId: this.jobId,
          newService: {
            productId: this.productId,
            quantity: this.quantity,
            price_purchasing: !nullOrUndefined(value) ? Number(value) : null,
            service: this.activeService.service,
          },
        });
      },
    },
    price_selling: {
      get() {
        return this.activeService.price_selling !== undefined
          ? this.activeService.price_selling
          : this.selectedService.price_selling;
      },
      set(value) {
        this.$store.dispatch(SERVICE_SELECTED_SPECIFY, {
          elementIndex: this.elementIndex,
          jobId: this.jobId,
          newService: {
            productId: this.productId,
            quantity: this.quantity,
            price_selling: !nullOrUndefined(value) ? Number(value) : null,
            service: this.activeService.service,
          },
        });
      },
    },
    price_selling_style() {
      let style = null;
      if (
        this.currentInvoicingTarget &&
        ['Affiliate', 'Brand', 'Brand_1', 'Brand_2'].includes(
          this.currentInvoicingTarget,
        )
      ) {
        style = { ...style, 'background-color': '#D4EDDA' };
      }
      if (this.isVariantPriceSelling) {
        style = { ...style, border: '2px solid #fd7e14' };
      }
      return style;
    },
    differentInvoicingTargetStyle() {
      let style = null;
      if (this.isDifferentInvoicingTarget) {
        style = { ...style, border: '2px solid #fd7e14' };
      }
      return style;
    },
    brandProjectId: {
      get() {
        return this.activeService ? this.activeService.brand_project_id : null;
      },
      set(value) {
        this.$store.dispatch(SERVICE_SELECTED_SPECIFY, {
          elementIndex: this.elementIndex,
          jobId: this.jobId,
          newService: {
            productId: this.productId,
            brand_project_id: value,
            service: this.activeService.service,
          },
        });
      },
    },
    stepQuantity() {
      if (
        this.selectedService &&
        ['a1t1p000001jvgrAAA'].includes(this.selectedService.sfid)
      ) {
        return 0.0001;
      }
      return 1;
    },
    minQuantity() {
      return this.selectedService && this.selectedService.minimum_quantity
        ? this.selectedService.minimum_quantity
        : 0;
    },
    maxQuantity() {
      let max = Number.MAX_SAFE_INTEGER;
      if (this.selectedService) {
        if (this.selectedService.one_or_more == 'one') {
          max = 1;
        } else if (this.selectedService.maximum_quantity) {
          max = this.selectedService.maximum_quantity;
        }
      }
      return max;
    },
    measure() {
      const measure = this.selectedService.unit_measure;
      if (measure && !['Count', 'Piece', 'Euro', '0,5 h'].includes(measure)) {
        return measure;
      } else {
        return '×';
      }
    },
    minEntryQuantity() {
      return this.selectedService && this.selectedService.minimum_quantity
        ? this.selectedService.minimum_quantity
        : 0;
    },
    maxEntryQuantity() {
      return this.selectedService && this.selectedService.maximum_quantity
        ? this.selectedService.maximum_quantity
        : Number.MAX_SAFE_INTEGER;
    },
    isVariantPriceSelling() {
      if (typeof this.activeService?.price_selling === 'number')
        return (
          this.activeService?.price_selling !=
          this.serviceDefinition?.price_selling
        );
      return false;
    },
    isVariantPriceLists() {
      if (typeof this.activeService?.price_lists === 'number')
        return (
          this.activeService?.price_lists != this.serviceDefinition?.price_lists
        );
      return false;
    },
    isVariantPricePurchasing() {
      if (typeof this.activeService?.price_purchasing === 'number')
        return (
          this.activeService?.price_purchasing !=
          this.serviceDefinition?.price_purchasing
        );
      return false;
    },
    serviceDefinition() {
      if (this.activeService && this.serviceDefinitions) {
        return this.serviceDefinitions[this.activeService.service];
      }
      return null;
    },
    serviceDefinitions() {
      return this.$store.getters.serviceDefinitions(this.jobId);
    },
    servicesGroup() {
      return this.$store.getters.servicesGroup({
        jobId: this.jobId,
        serviceSfid: this.activeService.service,
      });
    },
    selectedService() {
      if (this.serviceDefinition) {
        if (this.servicesGroup) {
          // Generate synthetic definition to group all possible quantities
          return Object.assign({}, this.serviceDefinition, {
            minimum_quantity: this.servicesGroup[0].minimum_quantity,
            maximum_quantity:
              this.servicesGroup[this.servicesGroup.length - 1]
                .maximum_quantity,
          });
        }
        return this.serviceDefinition;
      }
      return {};
    },
    total() {
      let price = 0;
      if (!this.hidePricePurchasing) price = this.price_purchasing;
      else if (!this.hidePriceSelling) price = this.price_selling;
      else if (!this.hidePriceList) price = this.price_lists;
      return (price || 0) * this.quantity;
    },
    usesValueEntry() {
      return (
        this.servicesGroup &&
        ['one', 'many'].includes(this.selectedService.one_or_more)
      );
    },
    usesQuantity() {
      if (this.selectedService.one_or_more === 'many') {
        return true;
      }
      return !this.usesValueEntry;
    },
    value_entry: {
      get() {
        return this.activeService.value_entry;
      },
      set(value) {
        this.$store.dispatch(SERVICE_SELECTED_SPECIFY, {
          elementIndex: this.elementIndex,
          jobId: this.jobId,
          newService: {
            quantity: this.quantity,
            value_entry: value,
            productId: this.productId,
            service: this.activeService.service,
          },
        });
      },
    },
    deleteVisible() {
      if (this.context == 'partner' && !this.isEditableForPartner) {
        return false;
      }
      if (!this.editServices || this.readonly) {
        return false;
      }
      return (
        this.context == 'admin' ||
        !this.isNotNew ||
        this.context == 'partner' ||
        this.isRequiredBy
      );
    },
    deleteDisabled() {
      const isServiceRequired = this.$store.getters.isServiceRequired({
        elementIndex: this.elementIndex,
        jobId: this.jobId,
      });

      const isPartnerDeleteDisabled =
        this.context === 'partner' &&
        this.isNotNew &&
        !this.isEditableForPartner;

      return isServiceRequired || isPartnerDeleteDisabled;
    },
    isEditableForPartner() {
      return this.serviceDefinition?.editable_for.includes('Partner');
    },
    job_sequence() {
      return this.$store.getters.jobInputValue(this.jobId, JOB_SEQUENCE);
    },
    requiredServices() {
      if (!this.activeService.service) {
        return [];
      }
      const serviceDefinitions = Object.values(
        this.$store.getters.serviceDefinitions(this.jobId),
      );
      return normalizeServiceList(serviceDefinitions, [
        {
          service: this.activeService.service,
          quantity: this.activeService.quantity,
        },
      ]).map((s) => ({
        id: s.service,
        quantity: s.quantity,
      }));
    },
    isDifferentInvoicingTarget() {
      if (this.activeService?.invoicing_target)
        return (
          this.activeService?.invoicing_target !=
          this.serviceDefinition?.invoicing_target
        );
      return false;
    },
    invoicingTarget: {
      get() {
        return this.activeService.invoicing_target;
      },
      set(value) {
        this.$store.commit(SERVICE_SELECTED_SPECIFY, {
          jobId: this.jobId,
          serviceIndex: this.elementIndex,
          newService: {
            quantity: this.quantity,
            productId: this.productId,
            service: this.activeService.service,
            invoicing_target: value,
          },
        });
      },
    },
    invoicingTargetEntity() {
      let entity = 'Customer';
      if (this.invoicingTarget.startsWith('Brand')) entity = 'Brand';
      if (this.invoicingTarget.startsWith('GID')) entity = 'Partner';
      return entity;
    },
    partiallySelection: function () {
      return loadPartialSelection(
        this.activeService.selections,
        SelectActionsEnum.PARTIAL_INVOICING,
      );
    },
    serviceSelectionDisabled() {
      return this.activeService?.productId ? true : false;
    },
    showInvoicingTarget() {
      return (
        this.activeService?.invoicing_target === InvoicingTargetEnum.BRAND_2 ||
        this.activeService?.invoicing_target === InvoicingTargetEnum.CUSTOMER_2
      );
    },
  },
  methods: {
    selectService(activeServiceSfid) {
      this.activeServiceSfid.set(activeServiceSfid);
    },
    deleteService() {
      this.$store.dispatch(SERVICE_SELECTED_ITEM_DELETE, {
        productId: this.productId,
        service: this.activeServiceSfid?.value,
        jobId: this.jobId,
        elementIndex: this.elementIndex,
      });
    },
    creditService() {
      const measure = this.selectedService.unit_measure;
      const prices = {
        price_purchasing:
          this.price_purchasing && this.creditPurchasingPrice
            ? -this.price_purchasing
            : undefined,
        price_selling:
          this.price_selling && this.creditSellingPrice
            ? -this.price_selling
            : undefined,
        price_lists:
          this.price_lists && this.creditListsPrice
            ? -this.price_lists
            : undefined,
      };

      const contact = this.$store.getters.user?.contact;
      const newService = {
        creator_role: this.context === 'admin' ? 'admin' : 'partner',
        creator_name: `${contact.first_name} ${contact.last_name}`,
        measure,
        quantity: this.quantity,
        invoicing_target: toGenericInvoicingTarget(
          this.selectedService.invoicing_target,
        ),
        service_type: this.selectedService.service_type,
        sort_index: null,
        headline: null,
        name: `Gutschrift ${this.selectedService.name}`,
        ...prices,
      };

      // Emit event to parent to add to custom services
      this.$emit('add-custom-service', newService);
      this.$refs.removeActionsDropdown.hide(true);
    },
    updateServiceGroup(newValue) {
      this.$nextTick(() => {
        if (this.servicesGroup) {
          for (let i = this.servicesGroup.length - 1; i > -1; i--) {
            const serviceDef = this.servicesGroup[i];
            if (newValue >= serviceDef.minimum_quantity) {
              if (serviceDef.sfid != this.service) {
                // refer to selectedService here
                this.inputChange('service', serviceDef.sfid);
              }
              break;
            }
          }
        }
      });
    },
    onPartialInvoiceChange(
      isSelected,
      action = SelectActionsEnum.PARTIAL_INVOICING,
    ) {
      const productState = isSelected ? PartialStateEnum.SCHEDULED : null;
      this.$store.dispatch(SERVICE_PRESELECT_AUTOMATION, {
        selected: isSelected,
        action,
        state: productState,
        item: this.activeService.id,
        serviceIndex: this.elementIndex,
        jobId: this.jobId,
      });
    },
  },
};
</script>

<style lang="scss">
.gid-tiny-col {
  flex-grow: 0.3 !important;
}
</style>
